import axios, { type AxiosError } from 'axios'

import { isReadRequest } from '../http/retry'

interface ApiErrorBody {
  message?: string
  error?: string
  // api-core's ReturnObj shape, used by the OTU and other v5 handlers, as
  // distinct from models.ErrorResponse ({ error, message }) and the auth
  // layer's ({ status, message }).
  error_message?: string
}

/**
 * Human copy for a response that carried no structured body, such as a proxy
 * error page. Without this, callers surface axios's own "Request failed with
 * status code 500" to a user. A request with no response at all is
 * transportMessage's case, not this one.
 */
function statusMessage(status: number): string | undefined {
  // Most callers are writes, so the copy must not assume a read.
  if (status === 403) return 'You do not have permission to do this.'
  if (status === 404) return 'The requested item could not be found.'
  if (status >= 500) return 'The server could not complete the request. Please try again.'
  return undefined
}

export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404
}

/** A write refused because the thing it would create already exists. */
export function isConflictError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409
}

/** api-core's error code (`error` in its ErrorResponse), when the reply carried one. */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined
  const data = error.response?.data as ApiErrorBody | undefined
  return data?.error
}

/** The backend's own reason, across the three shapes api-core returns. */
function bodyMessage(error: AxiosError): string | undefined {
  const data = error.response?.data as ApiErrorBody | undefined
  if (!data) return undefined
  // `||`, not `??`: an empty string is no reason, and must fall through to the
  // status copy and then the caller's fallback.
  return data.message || data.error || data.error_message || undefined
}

/**
 * Copy for a failure that carried no reason of its own.
 *
 * With no answer, a read can simply be tried again, but a write may already
 * have been done, and "try again" would do it twice. An inventory adjustment
 * is a delta, so a second submit adjusts twice (web-portal#98). A bodiless
 * gateway error is the same case seen through the proxy, typically a 504 while
 * api-core finished the write. The pages with more specific copy (finalize,
 * receive) still check isUnansweredError first.
 */
const WRITE_MAY_HAVE_HAPPENED =
  'The server did not answer in time, so this may still have gone through. Reload the page to check before trying again.'

// The gateway answered for api-core: it gave up waiting (504) or could not
// pass the request on (502, 503). api-core may have done the work regardless.
const GATEWAY_STATUSES = new Set([502, 503, 504])

function transportMessage(error: AxiosError): string | undefined {
  const isWrite = !isReadRequest(error.config)
  if (!error.response) {
    if (isUnansweredError(error) && isWrite) return WRITE_MAY_HAVE_HAPPENED
    return 'Could not reach the server. Check your connection and try again.'
  }
  if (isWrite && GATEWAY_STATUSES.has(error.response.status)) return WRITE_MAY_HAVE_HAPPENED
  return statusMessage(error.response.status)
}

/**
 * Whether a request got no answer at all: it timed out or the connection
 * dropped. For a write that means it may or may not have happened, which is
 * not the same thing as failing (web-portal#97). A request the caller canceled
 * is not one.
 */
export function isUnansweredError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED'
}

/**
 * Extracts a user-facing message from a failed API call. Prefers the backend's
 * structured error body ({ message, error }) so callers surface the real reason
 * (e.g., "a final audit has already been finalized today"), then copy for the
 * status, then the caller's fallback. An axios error's own message ("Request
 * failed with status code 400", "Network Error") is never shown. A non-axios
 * Error's message is.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(error)) {
    return bodyMessage(error) ?? transportMessage(error) ?? fallback
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}
